import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import {
  Delete, EditNote, Replay, Save,
} from '@mui/icons-material';
import { Link, useHistory, useLocation } from 'react-router-dom';
import {
  Button, Card, CardContent, Divider, Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import NotesManager from 'shared/components/data-display/notesManager';
import { BUTTON_COLORS, VARIANTS } from 'shared/constants';
import { Title, TooltipIconButton } from 'shared/components/abstract/interactables';
import { searchBuildings } from 'shared/api/buildings';
import { useRequest } from 'shared/hooks';
import { AsyncSearchableSelect } from 'shared/components/select/SearchableSelects';
import { TextEditor } from 'shared/components/data-display/editors';
import { GridWrapper } from 'shared/components/data-display/GridFormatter';

const initialState = {
  buildingCount: 0,
  editing: false,
  newName: '',
  selectedResource: null,
  updateCount: 0,
  editingNotes: false,
};

const resourceTypeToEntity = (rt) => {
  switch (rt) {
    case 'property manager':
      return 'Property Manager';
    case 'property complex':
      return 'Complex';
    default:
      return rt;
  }
};

/**
 * Returns a component that can do CRUD on a resource.
 * Resource is a model that a building has one of, E.G, a building has a manager.
 * Expects that the building search can filter for the resources id.
 * API response for resource must have id and name attributes.
 * @param {Object} config - Configuration object for the resource manager
 * @param {string} config.title - Title to display
 * @param {string} config.resourceType - Type of resource being managed
 * @param {string} config.filterKey - Key for filtering buildings by this resource
 * @param {boolean} [config.withNotes] - Whether to include notes management
 * @param {Object} serviceFunctions - Service functions for CRUD operations
 * @param {Function} serviceFunctions.getResource - Gets resource by id
 * @param {Function} serviceFunctions.searchResources - Searches resources
 * @param {Function} serviceFunctions.createResource - Creates a new resource
 * @param {Function} serviceFunctions.deleteResource - Deletes a resource
 * @param {Function} serviceFunctions.updateResource - Updates a resource
 * @returns {Function} ResourceManager component
 */
export default function withResourceManager(
  {
    title,
    resourceType,
    filterKey,
    withNotes,
  },
  {
    getResource,
    searchResources,
    createResource,
    deleteResource,
    updateResource,
  },
) {
  function ResourceManager() {
    const { request, isLoading } = useRequest();
    const { push: pushHistory } = useHistory();
    const { search } = useLocation();
    const [state, setState] = useState(initialState);
    const {
      buildingCount,
      editing,
      newName,
      selectedResource,
      updateCount,
    } = state;

    // Call the building search route with a limit of 0.
    // This will return the total number of buildings with the applied filter.
    useEffect(() => {
      (async () => {
        if (!selectedResource || !selectedResource.id) return;
        const response = await request(searchBuildings(`?l=0&${filterKey}=${selectedResource.id}`));
        if (response) {
          setState((current) => ({ ...current, buildingCount: response.total }));
        }
      })();
    }, [request, selectedResource]);

    // Keep the id in URL synced with selected resource.
    useEffect(() => {
      const id = parseInt(new URLSearchParams(search).get('id'), 10);
      if (selectedResource && (selectedResource.id === id || selectedResource.staged)) {
        return;
      }
      if (!id) {
        setState((current) => ({
          ...initialState,
          updateCount: current.updateCount,
        }));
        return;
      }
      (async () => {
        const result = await request(getResource(id));
        if (result) {
          setState((current) => ({
            ...current,
            editing: false,
            newName: result.name,
            selectedResource: result,
          }));
        }
      })();
    }, [request, search, selectedResource]);

    const setResource = useCallback((newResource) => {
      if (!newResource) {
        setState(initialState);
        pushHistory('?');
      } else {
        setState((current) => ({
          ...current,
          editing: false,
          newName: newResource.name,
          selectedResource: newResource,
        }));
        pushHistory(`?id=${newResource.id}`);
      }
    }, [pushHistory]);

    const onCreate = async (e) => {
      e.preventDefault();

      const response = await request(createResource(newName), `Created "${newName}"`);
      if (response) {
        pushHistory(`?id=${response.id}`);
        setState({
          buildingCount: 0,
          editing: false,
          newName: response.name,
          selectedResource: response,
          updateCount: updateCount + 1,
          editingNotes: false,
        });
      }
    };
    const onEditSave = async (e) => {
      e.preventDefault();
      const response = await request(updateResource(selectedResource.id, newName), `Set name to "${newName}"`);
      if (response) {
        setState((current) => ({
          ...current,
          updateCount: updateCount + 1,
          editing: false,
          selectedResource: response,
        }));
      }
    };
    const onDelete = async () => {
      const response = await request(deleteResource(selectedResource.id), `Deleted "${selectedResource.name}"`);
      if (response) {
        setState({ ...state, updateCount: updateCount + 1 });
        pushHistory('?');
      }
    };

    const stageItem = (name) => {
      setState({
        ...state,
        buildingCount: 0,
        editing: false,
        newName: name,
        selectedResource: { name, staged: true },
      });
    };

    return (
      <Grid container spacing={1}>
        <Title title={title} />
        <Grid size={{ xs: 12, md: 3 }}>
          <AsyncSearchableSelect
            creatable
            label={title}
            onChange={setResource}
            onCreate={stageItem}
            searchRequest={searchResources}
            selectedOption={selectedResource}
            updateCount={updateCount}
          />
        </Grid>
        <Grid size={12}>
          <Divider />
        </Grid>
        <Grid size={{ xs: 12, md: withNotes ? 6 : 4 }}>
          <Card sx={{ backgroundColor: 'cardBackground' }}>
            <CardContent>
              <Grid container spacing={1} sx={{ justifyContent: 'space-between' }}>
                <Grid size={{ xs: 12, md: withNotes ? 6 : 12 }}>
                  <ResourceForm
                    resourceType={resourceType}
                    filterKey={filterKey}
                    isLoading={isLoading}
                    editing={editing}
                    setEditing={() => setState({ ...state, editing: true })}
                    setNewName={(name) => setState({ ...state, newName: name })}
                    newName={newName}
                    saveResource={onEditSave}
                    createResource={onCreate}
                    deleteResource={onDelete}
                    resource={selectedResource}
                    buildingCount={buildingCount}
                    justifyActions={withNotes ? 'start' : 'end'}
                  />
                </Grid>
                {withNotes && selectedResource?.id && (
                  <>
                    <Divider orientation="vertical" variant="middle" flexItem />
                    <GridWrapper
                      title="Notes"
                      titleIcon={(
                        <Button
                          variant="outlined"
                          startIcon={<EditNote />}
                          onClick={() => setState({ ...state, editingNotes: true })}
                          size="small"
                        >
                          Edit
                        </Button>
)}
                      size={{ xs: 12, md: 5 }}
                    >
                      <NotesManager
                        title={`${resourceTypeToEntity(resourceType)} notes`}
                        entityID={selectedResource.id}
                        entityType={resourceTypeToEntity(resourceType)}
                        isEditing={state.editingNotes}
                        onStopEditing={() => setState({ ...state, editingNotes: false })}
                        onStartEditing={() => setState({ ...state, editingNotes: true })}
                      />
                    </GridWrapper>
                  </>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  }

  return ResourceManager;
}

/**
 * @param {string} resourceType - Type of resource
 * @param {string} filterKey - Filter key for building search
 * @param {boolean} isLoading - Whether a request is in progress
 * @param {Function} setNewName - Function to set the new name
 * @param {string} newName - The new name value
 * @param {Function} saveResource - Function to save the resource
 * @param {Function} createResource - Function to create a new resource
 * @param {Function} deleteResource - Function to delete the resource
 * @param {Object} [resource] - The resource object
 * @param {string} [resource.name] - Resource name
 * @param {number} [resource.id] - Resource ID
 * @param {boolean} [resource.staged] - Whether resource is staged for creation
 * @param {number} buildingCount - Number of buildings associated with this resource
 */
function ResourceForm({
  resourceType,
  filterKey,
  isLoading,
  setNewName,
  newName,
  saveResource,
  createResource,
  deleteResource,
  resource,
  buildingCount,
}) {
  const formButtons = useMemo(() => {
    if (resource?.staged) {
      return (
        <Grid>
          <Button
            type="submit"
            variant={VARIANTS.contained}
            startIcon={<Save />}
            disabled={isLoading || !newName || (!resource?.staged && resource?.name === newName)}
          >
            Create
          </Button>
        </Grid>
      );
    }
    if (resource?.name === newName) {
      return (
        <Grid>
          <TooltipIconButton
            onClick={deleteResource}
            message={`Delete ${resource?.name}`}
            icon={Delete}
            disabled={isLoading || buildingCount > 0 || resource?.staged}
            disabledTip="Cannot delete while name is changed or resource is associated to buildings"
            text="Delete"
            variant={VARIANTS.contained}
            color={BUTTON_COLORS.error}
          />
        </Grid>
      );
    }
    return (
      <Grid container sx={{ justifyContent: 'end' }}>
        <Grid>
          <Button
            startIcon={<Replay />}
            disabled={resource?.name === newName}
            onClick={() => setNewName(resource?.name)}
            variant={VARIANTS.outlined}
          >
            Reset
          </Button>
        </Grid>
        <Grid>
          <Button
            type="submit"
            variant={VARIANTS.contained}
            startIcon={<Save />}
            disabled={isLoading || !newName || (!resource?.staged
            && resource?.name === newName)}
          >
            Save
          </Button>
        </Grid>
      </Grid>

    );
  }, [resource, newName, isLoading, buildingCount, deleteResource, setNewName]);

  if (!resource) {
    return (
      <Typography sx={{
        mt: 1,
        fontStyle: 'italic',
      }}
      >
        {`Select or create a ${resourceType}`}
      </Typography>
    );
  }
  return (
    <Grid>
      <form onSubmit={resource?.staged ? createResource : saveResource}>
        <Grid container spacing={0.8}>
          <GridWrapper title={resourceTypeToEntity(resourceType)} size={12}>
            <Grid>
              <TextEditor
                label={resourceTypeToEntity(resourceType)}
                setValue={setNewName}
                disabled={resource?.staged}
                value={newName}
              />
            </Grid>
          </GridWrapper>
          <Grid container size={12} sx={{ justifyContent: 'space-between' }}>
            {buildingCount > 0 && (
              <Grid size={6}>
                <Typography sx={{ color: 'textMuted', fontStyle: 'italic' }}>
                  Associated to
                  <Link to={`/building/view?${filterKey}=${resource.id}`}>
                    &nbsp;
                    {buildingCount}
                    &nbsp;
                  </Link>
                  {buildingCount > 1 ? 'buildings' : 'building'}
                </Typography>
              </Grid>
            )}
            {!buildingCount && !resource?.staged && (
              <Grid size={6}>
                <em>No buildings associated</em>
              </Grid>
            )}
            {formButtons}
          </Grid>
        </Grid>
      </form>
    </Grid>
  );
}
